import React, { Suspense, useEffect, useState, useMemo, useRef } from 'react';

import { useSelector, useDispatch } from 'react-redux';

import EnergyOverviewTab from './Tabs/EnergyOverviewTab';
import EnergyTreeGraphTab from './Tabs/EnergyTreeGraphTab';
import EnergyAnalysisTab from './Tabs/EnergyAnalysisTab';
import InitiativesTab from './Tabs/InitiativesTab';
import EsgOverviewTab from './Tabs/EsgOverviewTab';
import DurationAnalysisTab from './Tabs/DurationAnalysisTab';
import HourAnalysisTab from './Tabs/HourAnalysisTab';
import ChildAnalysisTab from './Tabs/ChildAnalysisTab';
import ComparisonAnalysisTab from './Tabs/ComparisonAnalysisTab';
import StandardAnalysisTab from './Tabs/StandardAnalysisTab';
import { useLocalStorage } from '../../../hooks/useLocalStorage';

import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  Container,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  Skeleton,
} from '@mui/material';

import UnfoldMoreRoundedIcon from '@mui/icons-material/UnfoldMoreRounded';

import { getSelectedAccount } from '../../../redux/selectedAccountSlice';

import { useGetUnitsByAccountQuery } from '../../../api/recalc/endpoints/unitsApiSlice';
import { useLazyGetLocationQuery } from '../../../api/dataforsyningen/dataforsyningenApiSlice';
import { useLazyGetDataQuery } from '../../../api/recalc/endpoints/dataApiSlice';
import { useNavigate } from 'react-router-dom';
import { showSnackbar } from '../../../redux/snackbarSlice';

const CreateEnergyGroupDialog = React.lazy(() => import('remote/CreateEnergyGroupDialog').catch(() => ({ default: () => <div>Failed to load</div> })));
const EditEnergyGroupDialog = React.lazy(() => import('remote/EditEnergyGroupDialog').catch(() => ({ default: () => <div>Failed to load</div> })));
const DeleteEnergyGroupDialog = React.lazy(() => import('remote/DeleteEnergyGroupDialog').catch(() => ({ default: () => <div>Failed to load</div> })));
const NoEnergyGroupFoundCard = React.lazy(() => import('remote/NoEnergyGroupFoundCard').catch(() => ({ default: () => <div>Failed to load</div> })));
const AppBarMenu = React.lazy(() => import('remote/AppBarMenu').catch(() => ({ default: () => <div>Failed to load</div> })));

import {
  usePostLocationMutation,
  useGetLocationByIdQuery
} from '../../../api/recalcV2/endpoints/locationsApiSlice';

import {
  usePostEnergyGroupMutation,
  useGetEnergyGroupsQuery,
  usePutEnergyGroupOptionsMutation,
  useDeleteEnergyGroupMutation
} from '../../../api/recalcV2/endpoints/energyGroupsApiSlice';

import {
  useGetAlarmsByAccountQuery,
  useGetAlarmsFeedByAccountQuery
} from '../../../api/recalc/endpoints/alarmsApiSlice';

import { DateTime } from 'luxon';

import PowerLoadProfilesAnalysisTab from './Tabs/PowerLoadProfilesAnalysisTab';

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && (
        <Box sx={{ py: 2 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

export const formatTimestampForAPI = (date) => {
  return DateTime.fromISO(date, { zone: 'utc' }) // Ensure UTC interpretation
    .startOf('hour') // Round down to the nearest whole hour
    .toUTC() // Convert explicitly to UTC
    .toFormat("yyyy-MM-dd'T'HH:mm:ssZZ"); // Format as required
};

function formatAndEncodeTimestampForAPI(date) {
  const isoString = DateTime.fromJSDate(date).toISO(); // Uses the local time zone by default
  return encodeURIComponent(isoString); // Encode for URL
}

function extractUnitIds(data) {
  let unitIds = [];

  function extractFromObject(obj) {
    if (obj && typeof obj === "object") {
      if (Array.isArray(obj.dataStreams)) {
        unitIds.push(...obj.dataStreams.map(stream => stream.unitId).filter(id => id !== null && id !== undefined));
      }
      for (const key in obj) {
        extractFromObject(obj[key]);
      }
    }
  }

  if (Array.isArray(data)) {
    data.forEach(item => extractFromObject(item));
  } else if (typeof data === "object") {
    extractFromObject(data);
  }

  return unitIds;
}

function getUnitQueryOptions(unitTypeId) {
  const switchCase = {
    61: { unitTypeInputId: 6101, dataType: 'power-consumption' },
    28: { unitTypeInputId: 2801, dataType: 'apparent-power' },
    30: { unitTypeInputId: null, dataType: 'power' },
  };

  return switchCase[unitTypeId] || null;
}

const DateRangeSelector = React.lazy(() => import('remote/DateRangeSelector').catch(() => ({ default: () => <div>Failed to load</div> })));

const EnergyXplorerPage = () => {

  const [energyGroupId, setEnergyGroupId] = useLocalStorage('energyGroupId', null);

  //===Redux===//
  const selectedAccount = useSelector(getSelectedAccount);
  const dispatch = useDispatch();

  //===State===//
  const [selectedEnergyGroup, setSelectedEnergyGroup] = useState(null);
  const [value, setValue] = useState(0);
  const [selectedSliceMode, setSelectedSliceMode] = useState("Total");
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [nodeToAnalyse, setNodeToAnalyse] = useState(null);
  const [analysisMode, setAnalysisMode] = useState(null); // or 'seu'

  const [seuToAnalyze, setSeuToAnalyze] = useState(null);
  const [openCreateEnergyGroupDialog, setOpenCreateEnergyGroupDialog] = useState(false);
  const [deleteEnergyGroupDialogOpen, setDeleteEnergyGroupDialogOpen] = useState(false);
  const [openEditEnergyGroupDialog, setOpenEditEnergyGroupDialog] = useState(false);
  const [unitDataResults, setUnitDataResults] = useState([]);


  //===API v1===//
  const { data: units, isLoading: unitsLoading, isFetching: isFetchingUnits } = useGetUnitsByAccountQuery(selectedAccount?.AccountId, { skip: !selectedAccount?.AccountId });

  const [getData, { isLoading: isLoadingData, isFetching: isFetchingData }] = useLazyGetDataQuery();

  const [triggerGetLocation, { data, isFetching, error }] = useLazyGetLocationQuery();


  const { data: alarms, isLoading: isLoadingAlarms } = useGetAlarmsByAccountQuery(selectedAccount?.AccountId, { skip: !selectedAccount?.AccountId });
  const { data: alarmsFeed, isLoading: isLoadingAlarmsFeed } = useGetAlarmsFeedByAccountQuery(selectedAccount?.AccountId, { skip: !selectedAccount?.AccountId });

  //===API v2===//
  const [postLocation] = usePostLocationMutation();

  const [postEnergyGroup] = usePostEnergyGroupMutation();
  const [deleteEnergyGroup] = useDeleteEnergyGroupMutation();
  const [putEnergyGroupOptions] = usePutEnergyGroupOptionsMutation();

  const { data: locationData, isLoading: isLoadingLocation } = useGetLocationByIdQuery(
    {
      locationId: selectedEnergyGroup?.locationId,
      accountId: selectedAccount?.AccountId
    },
    { skip: !selectedEnergyGroup?.locationId }
  );

  const { data: allEnergyGroups, isLoading: isLoadingAllEnergyGroups, error: AllEnergyGroupsError } = useGetEnergyGroupsQuery(selectedAccount?.AccountId, {
    skip: !selectedAccount?.AccountId,
  });



  const [timeRange, setTimeRange] = useState({
    start: DateTime.now().startOf('day').minus({ weeks: 4 }).toUTC().toMillis(),
    end: DateTime.now().startOf('hour').toUTC().toMillis(),
  });

  const [timestampSummary, setTimestampSummary] = useState(null);
  const [duration, setDuration] = useState(14); // Default duration of 14 days

  const [esgTimestampSummary, setEsgTimestampSummary] = useState(null);
  const [esgDuration, setEsgDuration] = useState(14); // Default duration of 14 days

  const [esgTimeRange, setEsgTimeRange] = useState({
    start: DateTime.now().startOf('year').minus({ years: 1 }).toUTC().toMillis(),
    end: DateTime.now().endOf('day').minus({ days: 1 }).toUTC().toMillis(),
  });

  const [energyGroupsStats, setEnergyGroupStats] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSaveChanges = async (updatedGroup) => {
    if (!selectedEnergyGroup?.energyGroupId) return;

    const options = updatedGroup.options || {};

    try {
      const optionsResponse = await putEnergyGroupOptions({
        accountId: selectedAccount.AccountId,
        energyGroupId: selectedEnergyGroup.energyGroupId,
        options: options,
      }).unwrap();
    } catch (error) {
      dispatch(showSnackbar({
        message: 'Der opstod en fejl i opdatering af gruppe indstillinger',
        severity: 'error',
      }));
    }
  };

  useEffect(() => {
    if (!allEnergyGroups) return;

    const stats = allEnergyGroups?.map(group => {
      const totalNodes = group?.energyTreeNodes?.reduce((acc, node) => acc + 1, 0);
      return {
        id: group.energyGroupId,
        name: group.name,
        nodes: totalNodes,
        accountId: group.accountId,
      };
    });
    setEnergyGroupStats(stats);
  }, [allEnergyGroups])

  useEffect(() => {
    if (selectedAccount && allEnergyGroups) {

      const energyGroupsOnAccount = allEnergyGroups.filter(group => group.accountId === selectedAccount.AccountId);

      const selectedEnergyGroupFromStorage = energyGroupsOnAccount.find(group => group.energyGroupId === energyGroupId);

      if (selectedEnergyGroupFromStorage) {

        setSelectedEnergyGroup(selectedEnergyGroupFromStorage);
      } else if (energyGroupsOnAccount.length > 0) {

        setSelectedEnergyGroup(energyGroupsOnAccount[0]);
        setEnergyGroupId(energyGroupsOnAccount[0].energyGroupId);
      } else {
        setSelectedEnergyGroup(null);
      }
    }
  }, [allEnergyGroups, selectedAccount]);

  useEffect(() => {
    if (value !== 3 && value !== 4 && value !== 1) {
      setValue(0);
    }
  }, [selectedAccount, selectedEnergyGroup]);

  const unitIds = useMemo(() => {
    if (!selectedEnergyGroup?.energyTreeNodes) return [];
    return extractUnitIds(selectedEnergyGroup?.energyTreeNodes);
  }, [selectedEnergyGroup]);

  const handleAlarmClick = (alarm) => {
    // navigate('/alarms');
  }

  const handleDeleteEnergyGroup = async (energyGroupId) => {
    try {
      const response = await deleteEnergyGroup({ energyGroupId: energyGroupId, accountId: selectedAccount.AccountId }).unwrap();
      dispatch(showSnackbar({ message: 'Gruppe slettet', severity: 'success' }));
      setDeleteEnergyGroupDialogOpen(false);
    } catch (error) {
      dispatch(showSnackbar({ message: 'Der opstod en fejl i sletning af gruppe', severity: 'error' }));
    }
  };

  const handleAddGroup = async (newGroup) => {

    if (!newGroup.location) return;

    try {
      const locationResponse = await postLocation({ accountId: selectedAccount.AccountId, location: newGroup.location }).unwrap();

      const energyGroupData = {
        name: newGroup.name,
        accountId: selectedAccount.AccountId,
        locationId: locationResponse.locationId,
        options: newGroup.options || {}, // Include options if provided
      };

      let createdGroup = null;

      try {
        createdGroup = await postEnergyGroup({
          energyGroup: energyGroupData,
          accountId: selectedAccount.AccountId,
        }).unwrap();

      } catch (error) {
        // Attempt to extract created group info from error (if backend returns it in body)
        if (error?.status === 500 && error?.data?.energyGroupId) {
          createdGroup = error.data;
          dispatch(showSnackbar({
            message: 'Gruppe oprettet, men der opstod en fejl (500). Fortsætter med indstillinger...',
            severity: 'warning',
          }));
        } else {
          dispatch(showSnackbar({
            message: 'Der opstod en fejl i oprettelse af gruppe',
            severity: 'error',
          }));
        }
      }

      if (!createdGroup?.energyGroupId) {
        console.error("No valid energy group ID found. Aborting.");
        return;
      }

    } catch (error) {
      console.error("Error posting location:", error);
      dispatch(showSnackbar({
        message: 'Der opstod en fejl i oprettelse af lokation',
        severity: 'error',
      }));
    }
  };


  const handleSEURowClick = (seu) => {
    setSeuToAnalyze(seu);
    setAnalysisMode('seu');
    if (value === 0) {
      setValue(2);
    }
  };

  const handleNodeClick = (node) => {
    setNodeToAnalyse(node);
    setAnalysisMode('node');
    if (value === 0) {
      setValue(2);
    }
  };

  const fetchRawDataForUnit = async (id) => {
    if (!units) return null;

    const unit = units.find((unit) => unit.UnitId === id);
    if (!unit) return { unit: null, data: [] };

    const unitTypeId = unit?.UnitType?.UnitTypeId;
    const queryOptions = getUnitQueryOptions(unitTypeId);

    try {
      const fetchedData = await getData({
        from: formatAndEncodeTimestampForAPI(new Date(timeRange.start)),
        to: formatAndEncodeTimestampForAPI(new Date(timeRange.end)),
        unitId: id,
        datatype: queryOptions?.dataType,
        unitTypeInputId: queryOptions?.unitTypeInputId,
        aggregateType: 'Hour',
      }).unwrap();

      return { unit, data: fetchedData || [] };
    } catch (error) {
      console.error(`Failed to fetch data for unit ${id}: ${error.message}`);
      dispatch(showSnackbar({ message: 'Fejl ved hentning af data', severity: 'error' }));
      return { unit, data: [] };
    }
  };

  useEffect(() => {
    if (!unitIds || unitIds.length === 0 || !getData) return;

    const fetchDataForAllUnits = async () => {
      try {
        const results = await Promise.all(
          unitIds.map(async (id) => {
            const result = await fetchRawDataForUnit(id);
            return result;
          })
        );
        setUnitDataResults(results); // Store the results in state if needed
      } catch (error) {
        console.error("Error fetching data for units:", error);
      }
    };

    fetchDataForAllUnits();
  }, [unitIds, units, timeRange]);

  const [showLoading, setShowLoading] = useState(false);

  const loading = useMemo(() => {
    return (
      unitsLoading ||
      isFetchingUnits ||
      isLoadingData ||
      isFetchingData
      // isLoadingEnergyGroups
    );
  }, [
    unitsLoading,
    isFetchingUnits,
    // isLoadingEnergyGroups,
    selectedAccount,
    selectedEnergyGroup,
    timeRange,
    isFetchingData,
    isLoadingData,
    getData
  ]);

  const loadingMessage = useMemo(() => {
    if (unitsLoading || isFetchingUnits) return 'Indlæser units...';
    if (isLoadingData, isFetchingData) return 'Indlæser data...';
    if (loading) return 'Gør klar...';
    return null;
  }, [loading]);

  // useEffect(() => {
  //   const timer = setTimeout(() => setShowLoading(loading), 200);
  //   return () => clearTimeout(timer);
  // }, [loading]);

  const handleEnergyGroupChange = (event, value) => {
    setSelectedEnergyGroup(value);
    setEnergyGroupId(value?.id || null); // Update local storage with the selected energy group ID
  };

  const handleEnergyGroupCreate = () => {
    setOpenCreateEnergyGroupDialog(true);
  };

  const handleEnergyGroupSettingsClick = () => {
    setOpenEditEnergyGroupDialog(true);
  };

  useEffect(() => {
    if (!selectedEnergyGroup) return;
    if (!selectedEnergyGroup?.energyTreeNodes?.length > 0) {
      setValue(1); // Switch to the Energy Tree Graph tab when an energy group is selected
    }
  }, [selectedEnergyGroup, value]);

  return (
    <Container maxWidth="xxl">
      <Suspense fallback={<div>Loading EnergyGroupSettings...</div>}>
        <AppBarMenu
          setValue={setValue}
          alarms={alarmsFeed}
          value={value}
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          selectedEnergyGroup={selectedEnergyGroup}
          handleClose={handleClose}
          handleClick={handleClick}
          handleAlarmClick={handleAlarmClick}
          handleEnergyGroupCreate={handleEnergyGroupCreate}
          handleEnergyGroupSettingsClick={handleEnergyGroupSettingsClick}
          setDeleteEnergyGroupDialogOpen={setDeleteEnergyGroupDialogOpen}
          energyGroupsStats={energyGroupsStats}
        />
      </Suspense>

      {!allEnergyGroups?.length > 0 ? (
        <Suspense fallback={<div>Loading...</div>}>
          <NoEnergyGroupFoundCard />
        </Suspense>
      ) : (

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'left', gap: 1 }}>
              <Autocomplete
                options={allEnergyGroups || []}
                getOptionLabel={(option) => option.name}
                value={selectedEnergyGroup || null}
                sx={{ minWidth: '250px', borderColor: 'divider', }}
                popupIcon={<UnfoldMoreRoundedIcon />}
                onChange={handleEnergyGroupChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Vælg energi gruppe"
                    variant="outlined"
                    sx={{
                      '& .MuiInputBase-root': {
                        borderRadius: '10px',
                        backgroundColor: 'background.paper',
                      },
                      '& .MuiInputBase-root:hover': {
                        borderRadius: '10px',
                      },
                      '& .MuiFilledInput-root': {
                        borderRadius: '5px',
                      },
                      '& .MuiFilledInput-underline:before, & .MuiFilledInput-underline:after': {
                        display: 'none',
                      },
                      '& .MuiInputLabel-root': {
                        color: 'gray',
                      },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'divider',
                        },
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'divider',
                      },
                      '& .MuiInputLabel-root': {
                        color: 'gray',
                      },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      disableUnderline: true,
                    }}
                  />
                )}
              />

              {value !== 3 &&
                <Box sx={{ display: 'flex', justifyContent: 'left' }}>
                  <Suspense fallback={
                    <Stack spacing={1} sx={{ width: '100%' }}>
                      <Skeleton variant="text" width={100} />
                    </Stack>
                  }>
                    {value === 4 ?
                      // Esg daterangeselector
                      <DateRangeSelector setTimeRange={setEsgTimeRange} timeRange={esgTimeRange} setDuration={setEsgDuration} timestampSummary={esgTimestampSummary} enableEsgShortcuts />
                      :
                      // Normal datarangeselector
                      <DateRangeSelector setTimeRange={setTimeRange} timeRange={timeRange} setDuration={setDuration} timestampSummary={timestampSummary} />
                    }
                  </Suspense>
                </Box>
              }
            </Box>
          </Box>

        </Box>
      )}

      <TabPanel value={value} index={0}>
        {showLoading ?
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <Card
              sx={{
                width: { xs: '90%', sm: '60%', md: '50%', lg: '40%' },
                maxWidth: '500px',
                minWidth: '300px',
                p: 3,
                borderRadius: 2,
                mt: 8,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
              variant="outlined"
            >
              <CardContent
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'left', mb: 4 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontFamily: 'JetBrains Mono NL',
                      color: (theme) => theme.palette.mode === 'light' ? 'hsl(0, 0%, 20%)' : 'hsl(0, 0%, 85%)',
                      fontWeight: 'bold'
                    }}
                  >
                    EnergyXplorer
                  </Typography>
                </Box>
                <CircularProgress />
                <Typography variant="h6" sx={{ mt: 2, textAlign: 'center' }}>
                  {loadingMessage}
                </Typography>
              </CardContent>
            </Card>
          </Box>
          : (
            <>
              <EnergyOverviewTab
                selectedAccount={selectedAccount}
                selectedEnergyGroup={selectedEnergyGroup}
                timeRange={timeRange}
                handleSEURowClick={handleSEURowClick}
                setTimestampSummary={setTimestampSummary}
              />
            </>
          )}
      </TabPanel>

      <TabPanel value={value} index={1}>
        {selectedEnergyGroup && !loading && (
          <EnergyTreeGraphTab
            selectedEnergyGroup={selectedEnergyGroup}
            unitDataResults={unitDataResults}
            units={units}
            isLoading={isLoadingData || isFetchingData}
            setSelectedSliceMode={setSelectedSliceMode}
            selectedSliceMode={selectedSliceMode}
            setNodeToAnalyse={handleNodeClick}
            setValue={setValue}
          />
        )}
      </TabPanel>

      <TabPanel value={value} index={2}>
        {selectedEnergyGroup && (
          <EnergyAnalysisTab
            selectedAccount={selectedAccount}
            focusedNode={nodeToAnalyse}
            focusedSeu={seuToAnalyze}
            initialMode={analysisMode}
            selectedEnergyGroup={selectedEnergyGroup}
            setTimestampSummary={setTimestampSummary}
            duration={duration}
            timeRange={timeRange}
          />
        )}
      </TabPanel>

      <TabPanel value={value} index={21}>
        {selectedEnergyGroup && (
          <ComparisonAnalysisTab
            selectedAccount={selectedAccount}
            selectedEnergyGroup={selectedEnergyGroup}
            timeRange={timeRange}
            duration={duration}
          />
        )}
      </TabPanel>

      <TabPanel value={value} index={23}>
        {selectedEnergyGroup && (
          <DurationAnalysisTab
            selectedAccount={selectedAccount}
            setTimestampSummary={setTimestampSummary}
            selectedEnergyGroup={selectedEnergyGroup}
            timeRange={timeRange}
          />
        )}
      </TabPanel>

      <TabPanel value={value} index={24}>
        {selectedEnergyGroup && (
          <HourAnalysisTab
            selectedAccount={selectedAccount}
            setTimestampSummary={setTimestampSummary}
            selectedEnergyGroup={selectedEnergyGroup}
            timeRange={timeRange}
          />
        )}
      </TabPanel>

      <TabPanel value={value} index={25}>
        {selectedEnergyGroup && (
          <ChildAnalysisTab
            selectedAccount={selectedAccount}
            setTimestampSummary={setTimestampSummary}
            selectedEnergyGroup={selectedEnergyGroup}
            timeRange={timeRange}
          />
        )}
      </TabPanel>

      <TabPanel value={value} index={26}>
        {selectedEnergyGroup && (
          <StandardAnalysisTab
            selectedEnergyGroup={selectedEnergyGroup}
            selectedAccount={selectedAccount}
            timeRange={timeRange}
            setTimestampSummary={setTimestampSummary}
          />
        )}
      </TabPanel>
      <TabPanel value={value} index={27}>
        {selectedEnergyGroup && (
          <PowerLoadProfilesAnalysisTab
            selectedEnergyGroup={selectedEnergyGroup}
            selectedAccount={selectedAccount}
            timeRange={timeRange}
            setTimestampSummary={setTimestampSummary}
          />
        )}
      </TabPanel>
      <TabPanel value={value} index={3}>
        {selectedEnergyGroup && (
          <InitiativesTab
            selectedEnergyGroup={selectedEnergyGroup}
            // isLoadingEnergyGroups={isLoadingEnergyGroups || isFetchingEnergyGroups}
            isLoadingData={isLoadingData}
            loadingMessage={loadingMessage}
            unitData={unitDataResults}
          />
        )}
      </TabPanel>

      <TabPanel value={value} index={4}>
        {selectedEnergyGroup && (
          <EsgOverviewTab
            selectedEnergyGroup={selectedEnergyGroup}
            selectedAccount={selectedAccount}
            timeRange={esgTimeRange}
            setTimeRange={setEsgTimeRange}
          />
        )}
      </TabPanel>

      <Suspense fallback={<div>Loading EnergyGroupSettings...</div>}>
        <CreateEnergyGroupDialog
          open={openCreateEnergyGroupDialog}
          onClose={() => setOpenCreateEnergyGroupDialog(false)}
          onCreate={handleAddGroup}
          triggerGetLocation={triggerGetLocation}
          selectedAccount={selectedAccount}
        />
      </Suspense>

      <Suspense fallback={<div>Loading EnergyGroupSettings...</div>}>
        <EditEnergyGroupDialog
          open={openEditEnergyGroupDialog}
          onClose={() => setOpenEditEnergyGroupDialog(false)}
          triggerGetLocation={triggerGetLocation}
          selectedEnergyGroup={selectedEnergyGroup}
          locationData={locationData}
          onUpdate={handleSaveChanges}
        />
      </Suspense>

      <Suspense fallback={<div>Loading EnergyGroupSettings...</div>}>
        <DeleteEnergyGroupDialog
          open={deleteEnergyGroupDialogOpen}
          onClose={() => setDeleteEnergyGroupDialogOpen(false)}
          selectedEnergyGroup={selectedEnergyGroup}
          handleDeleteEnergyGroup={handleDeleteEnergyGroup}
          handleClose={handleClose}
        />
      </Suspense>
    </Container>
  );
}

export default EnergyXplorerPage;